import {create} from 'zustand';

export interface MapState {
  center: { lat: number; lng: number };
  bounds: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } };
  zoom: number;
  isLoading: boolean;
  error?: string;
}

interface MapStore extends MapState {
  // Actions
  setCenter: (lat: number, lng: number) => void;
  setCenterAndBounds: (lat: number, lng: number, zoom?: number) => void;
  setBounds: (bounds: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } }) => void;
  setZoom: (zoom: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error?: string) => void;
  reset: () => void;
}

// دوال مساعدة للمقارنة
const areCoordinatesEqual = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  return Math.abs(a.lat - b.lat) < 0.0001 && Math.abs(a.lng - b.lng) < 0.0001;
};

const areBoundsEqual = (
  a: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } },
  b: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } }
) => {
  return (
    areCoordinatesEqual(a.sw, b.sw) && areCoordinatesEqual(a.ne, b.ne)
  );
};

// دالة لحساب الحدود من المركز والزوم
const calculateBoundsFromCenter = (lat: number, lng: number, zoom: number = 10) => {
  // تقدير تقريبي للحدود بناءً على الزوم
  // كلما قل الزوم، كلما زادت المساحة المغطاة
  const latOffset = (15 - zoom) * 0.05; // تقدير تقريبي
  const lngOffset = (15 - zoom) * 0.05;

  return {
    sw: {lat: lat - latOffset, lng: lng - lngOffset},
    ne: {lat: lat + latOffset, lng: lng + lngOffset}
  };
};

// الحالة الافتراضية
const defaultState: MapState = {
  center: {lat: 24.7136, lng: 46.6753}, // الرياض
  bounds: {
    sw: {lat: 24.55, lng: 46.50},
    ne: {lat: 24.90, lng: 46.85}
  },
  zoom: 10,
  isLoading: false,
  error: undefined
};

export const useMapStore = create<MapStore>((set, get) => ({
  ...defaultState,

  setCenter: (lat, lng) => {
    const currentCenter = get().center;
    if (!areCoordinatesEqual(currentCenter, {lat, lng})) {
      set({center: {lat, lng}});
    }
  },

  setCenterAndBounds: (lat, lng, zoom = 10) => {
    const currentCenter = get().center;
    const currentZoom = get().zoom;
    const useZoom = zoom || currentZoom;

    if (!areCoordinatesEqual(currentCenter, {lat, lng})) {
      const newBounds = calculateBoundsFromCenter(lat, lng, useZoom);
      set({
        center: {lat, lng},
        bounds: newBounds,
        zoom: useZoom
      });
    }
  },

  setBounds: (bounds) => {
    const currentBounds = get().bounds;
    if (!areBoundsEqual(currentBounds, bounds)) {
      set({bounds});
    }
  },

  setZoom: (zoom) => {
    const currentZoom = get().zoom;
    if (currentZoom !== zoom) {
      set({zoom});
    }
  },

  setLoading: (loading) => {
    const currentLoading = get().isLoading;
    if (currentLoading !== loading) {
      set({isLoading: loading});
    }
  },

  setError: (error) => {
    const currentError = get().error;
    if (currentError !== error) {
      set({error});
    }
  },

  reset: () => {
    set(defaultState);
  }
}));
